import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * The Oswald uppercase chips. `Visual - Demo.dc.html` draws them in exactly
 * two shapes:
 *
 *   pill  999px radius, `4px 11px`, 0.74rem semibold, a tinted background and
 *         a 1.5px border the design derives as the foreground at 40%
 *         (`1.5px solid ${fg}66`) — status pills, project status, page refs
 *   tag   5px radius, `3px 7px`, 0.62rem bold, solid fill and white text —
 *         the "Missing" / "Expired" / "Due soon" chips in the compliance
 *         banner rows, and the live-value pill on the Ask answer
 *
 * `StatusPill` in `components/gb` is the pre-bound compliance flavour of the
 * pill shape (with its leading dot); this is the general chip.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1.5 whitespace-nowrap font-display uppercase",
  {
    variants: {
      tone: {
        ok: "border-ok/40 bg-ok-bg text-ok",
        warn: "border-warn/40 bg-warn-bg text-warn",
        danger: "border-danger/40 bg-danger-bg text-danger",
        violet: "border-violet/40 bg-violet-bg text-violet",
        neutral: "border-border bg-subtle text-muted",
        brand: "border-brand/40 bg-brand-tint text-brand",
      },
      shape: {
        pill: [
          "rounded-full border-[1.5px] px-[11px] py-1",
          "text-[0.74rem] font-semibold tracking-[0.03em]",
        ],
        // 5px is the only radius in the design with no token; see theme.css.
        tag: [
          "rounded-tag border-[1.5px] border-transparent px-[7px] py-[3px]",
          "text-[0.62rem] font-bold tracking-[0.06em] text-white",
        ],
      },
    },
    compoundVariants: [
      // The tag chip is a solid fill of the tone's foreground, not its tint.
      { shape: "tag", tone: "ok", className: "bg-ok" },
      { shape: "tag", tone: "warn", className: "bg-warn" },
      { shape: "tag", tone: "danger", className: "bg-danger" },
      { shape: "tag", tone: "violet", className: "bg-violet" },
      { shape: "tag", tone: "neutral", className: "bg-muted" },
      { shape: "tag", tone: "brand", className: "bg-brand" },
    ],
    defaultVariants: { tone: "neutral", shape: "pill" },
  },
);

type BadgeProps = ComponentProps<"span"> & VariantProps<typeof badgeVariants>;

export function Badge({ className, tone, shape, ...props }: BadgeProps) {
  return (
    <span
      data-slot="badge"
      className={cn(badgeVariants({ tone, shape }), className)}
      {...props}
    />
  );
}

export { badgeVariants };
export type { BadgeProps };
